import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { QRCodeSVG } from 'qrcode.react';
import supabase from '../supabase/supabase';
import { getVertical } from '../common/verticals';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPlus, FiX, FiFacebook, FiSmartphone, FiRefreshCw, FiMapPin, FiShield } = FiIcons;

const TABLE_CITIES = 'cities_1715634000000';
const TABLE_SLOTS  = 'slots_1715634000000';

// ── Barcode decoration ───────────────────────────────────────────────────────
// Simulates an Intelligent Mail Barcode (IMB) — full / ascender / descender / tracker bars
const BAR_PATTERN = [2,0,3,1,0,2,3,0,1,2,0,3,2,1,0,3,2,0,1,3,0,2,1,3,0,2,3,1,0,2,1,3,0,2,3,1,0,3,2,1];
function MailBarcode() {
  return (
    <div className="flex items-center gap-[1.5px]">
      {BAR_PATTERN.map((type, i) => {
        // 0=full, 1=ascender, 2=descender, 3=tracker
        const heights = ['h-5', 'h-4', 'h-4', 'h-2.5'];
        const offsets = ['', '', 'mt-1', 'mt-1'];
        return (
          <div
            key={i}
            className={`w-[2px] bg-brand-brown ${heights[type]} ${offsets[type]} shrink-0`}
          />
        );
      })}
    </div>
  );
}

// ── Slot card (shared for front and back) ────────────────────────────────────
function SlotCard({ slot, onClaim, small = false, borderClass = '', price = '' }) {
  return (
    <div
      onClick={() => !slot.is_reserved && onClaim(slot)}
      className={`relative flex flex-col items-center justify-center text-center transition-all duration-300 group overflow-hidden ${borderClass} ${
        slot.is_reserved
          ? 'bg-[#f0ece1] cursor-not-allowed'
          : 'bg-white border-2 border-dashed border-gray-300 hover:bg-brand-yellow/10 hover:border-solid hover:border-brand-yellow cursor-pointer'
      }`}
    >
      {!slot.is_reserved && (
        <span className={`font-black uppercase text-brand-brown tracking-tight relative z-20 group-hover:scale-110 transition-transform ${
          small ? 'text-[10px] md:text-xs' : 'text-sm md:text-base lg:text-lg'
        }`}>
          {slot.category}
        </span>
      )}

      {slot.is_reserved && (
        <div className="absolute inset-0 flex flex-col items-center justify-center z-10 gap-2">
          <div className={`border-[3px] bg-white border-brand-brown text-brand-brown font-black px-2 py-0.5 rotate-[-12deg] uppercase tracking-tighter shadow-[3px_3px_0px_rgba(74,51,32,1)] ${small ? 'text-[9px]' : 'text-sm md:text-lg'}`}>
            TAKEN
          </div>
          <span className={`font-black uppercase text-brand-brown/70 tracking-tight ${small ? 'text-[8px]' : 'text-[10px] md:text-xs'}`}>
            {slot.category}
          </span>
        </div>
      )}

      {!slot.is_reserved && (
        <>
          <div className={`rounded-lg flex items-center justify-center bg-brand-blue/10 text-brand-blue group-hover:bg-brand-yellow group-hover:text-brand-brown transition-all mt-1 ${small ? 'w-5 h-5' : 'w-8 h-8'}`}>
            <SafeIcon icon={FiPlus} className={small ? 'text-xs' : 'text-base'} />
          </div>
          {price && (
            <span className={`font-black text-brand-blue group-hover:text-brand-brown transition-colors mt-1 ${small ? 'text-[9px] md:text-[10px]' : 'text-xs md:text-sm'}`}>
              {price}
            </span>
          )}
        </>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function ReserveSpot({ citySlug = 'surprise' }) {
  const [frontSlots, setFrontSlots] = useState([]);
  const [backSlots,  setBackSlots]  = useState([]);
  const [cityName,   setCityName]   = useState('');
  const [cityState,  setCityState]  = useState('AZ');
  const [vertical,   setVertical]   = useState('event-planning');
  const [zipMapUrl,  setZipMapUrl]  = useState('');
  const [loading,    setLoading]    = useState(true);
  const [selectedSlot, setSelectedSlot] = useState(null);

  useEffect(() => { fetchData(); }, [citySlug]);

  const fetchData = async () => {
    setLoading(true);
    const { data: cityData } = await supabase
      .from(TABLE_CITIES)
      .select('id, name, zip_map_url, vertical, state')
      .eq('slug', citySlug)
      .single();

    if (cityData) {
      setCityName(cityData.name);
      setCityState(cityData.state ?? 'AZ');
      setVertical(cityData.vertical ?? 'event-planning');
      setZipMapUrl(cityData.zip_map_url ?? '');

      const { data: slotsData } = await supabase
        .from(TABLE_SLOTS)
        .select('*')
        .eq('city_id', cityData.id)
        .order('position');

      if (slotsData) {
        setFrontSlots(slotsData.filter(s => s.side !== 'back'));
        setBackSlots(slotsData.filter(s => s.side === 'back'));
      }
    }
    setLoading(false);
  };

  if (loading) return (
    <div className="h-96 flex items-center justify-center">
      <SafeIcon icon={FiRefreshCw} className="animate-spin text-4xl text-brand-blue" />
    </div>
  );

  const v = getVertical(vertical);
  const borderW = 'border-[6px] md:border-[10px]';
  const innerBorderW = 'border-r-[6px] md:border-r-[10px] border-brand-brown';
  const innerBorderB = 'border-b-[6px] md:border-b-[10px] border-brand-brown';

  return (
    <section id="reserve" className="py-24 bg-white overflow-hidden">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* ── Heading ── */}
        <div className="text-center mb-16 px-4">
          <span className="inline-block py-1.5 px-4 rounded-full bg-brand-blue/10 text-brand-blue text-xs font-black tracking-widest mb-4 uppercase">
            Live Availability: {cityName.toUpperCase()}
          </span>
          <h2 className="text-4xl md:text-6xl font-black text-brand-brown uppercase mb-6 tracking-tighter italic leading-none">
            Claim Your <br className="md:hidden"/> Category
          </h2>
          <p className="text-xl text-brand-brown/70 font-bold max-w-3xl mx-auto leading-relaxed">
            Exclusivity is guaranteed. Once a niche is taken, it's gone for that city's drop.
          </p>

          {/* Coverage map image */}
          {zipMapUrl && (
            <div className="mt-10 max-w-2xl mx-auto">
              <p className="text-sm font-black uppercase tracking-widest text-brand-brown/50 mb-4 flex items-center justify-center gap-2">
                <SafeIcon icon={FiMapPin} className="text-brand-blue" /> This mailing covers these areas
              </p>
              <div className="rounded-[1.5rem] overflow-hidden border-4 border-brand-brown/10 shadow-[12px_12px_0px_rgba(74,51,32,0.06)]">
                <img
                  src={zipMapUrl}
                  alt="Mailing coverage area"
                  className="w-full object-contain"
                />
              </div>
            </div>
          )}

        </div>

        {/* ── MAILER SLOTS ── */}
        <div className="mb-6 max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-5 bg-brand-brown text-white px-5 py-3 rounded-2xl">
            <span className="font-black uppercase tracking-widest text-sm">Available Ad Slots</span>
            <div className="flex-1 h-px bg-white/20" />
            <span className="text-xs font-black uppercase tracking-widest text-white/70">16 Slots · 2.6″ × 3.75″ · <span className="text-brand-yellow">$375 / slot</span></span>
          </div>
          <div className={`relative bg-[#Fdfbf7] ${borderW} border-brand-brown rounded-[1.5rem] shadow-[24px_24px_0px_rgba(74,51,32,0.08)] overflow-hidden aspect-[4/3] flex flex-col`}>
            <div className={`h-[44.5%] flex ${innerBorderB}`}>
              {frontSlots.slice(0, 4).map((slot, i) => (
                <SlotCard
                  key={slot.id}
                  slot={slot}
                  onClaim={setSelectedSlot}
                  price="$375 / slot"
                  borderClass={`flex-1 border-4 border-transparent ${i < 3 ? innerBorderW : ''}`}
                />
              ))}
            </div>
            <div className={`h-[11%] flex items-center justify-between px-6 md:px-10 ${innerBorderB}`} style={{ backgroundColor: v.hex }}>
              <span className="text-white font-black text-lg md:text-3xl uppercase tracking-tighter italic leading-none">
                {v.collective}
              </span>
              <span className="text-white border-[3px] border-white/40 bg-white/10 px-4 py-1 text-[10px] md:text-sm font-black uppercase tracking-tighter hidden sm:block">
                {cityName.toUpperCase()} BATCH
              </span>
            </div>
            <div className="h-[44.5%] flex">
              {frontSlots.slice(4, 8).map((slot, i) => (
                <SlotCard
                  key={slot.id}
                  slot={slot}
                  onClaim={setSelectedSlot}
                  price="$375 / slot"
                  borderClass={`flex-1 border-4 border-transparent ${i < 3 ? innerBorderW : ''}`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* ── BACK SIDE AD SLOTS ── */}
        {backSlots.length > 0 && (
          <div className="mb-6 max-w-5xl mx-auto">
            <div className="flex items-center gap-3 mb-5 bg-brand-brown text-white px-5 py-3 rounded-2xl">
              <span className="font-black uppercase tracking-widest text-sm">Back Side — Ad Slots</span>
              <div className="flex-1 h-px bg-white/20" />
              <span className="text-xs font-black uppercase tracking-widest text-white/70">8 Slots · 2.6″ × 3.75″ · <span className="text-brand-yellow">$375 / slot</span></span>
            </div>
            <div className={`relative bg-[#Fdfbf7] ${borderW} border-brand-brown rounded-[1.5rem] shadow-[24px_24px_0px_rgba(74,51,32,0.08)] overflow-hidden aspect-[4/3] flex flex-col`}>
              <div className={`h-[44.5%] flex ${innerBorderB}`}>
                {backSlots.slice(0, 4).map((slot, i) => (
                  <SlotCard
                    key={slot.id}
                    slot={slot}
                    onClaim={setSelectedSlot}
                    price="$375 / slot"
                    borderClass={`flex-1 border-4 border-transparent ${i < 3 ? innerBorderW : ''}`}
                  />
                ))}
              </div>
              <div className={`h-[11%] flex items-center justify-between px-6 md:px-10 ${innerBorderB}`} style={{ backgroundColor: v.hex }}>
                <span className="text-white font-black text-lg md:text-3xl uppercase tracking-tighter italic leading-none">
                  {v.collective}
                </span>
                <span className="text-white border-[3px] border-white/40 bg-white/10 px-4 py-1 text-[10px] md:text-sm font-black uppercase tracking-tighter hidden sm:block">
                  {cityName.toUpperCase()} BACK
                </span>
              </div>
              <div className="h-[44.5%] flex">
                {backSlots.slice(4, 8).map((slot, i) => (
                  <SlotCard
                    key={slot.id}
                    slot={slot}
                    onClaim={setSelectedSlot}
                    price="$375 / slot"
                    borderClass={`flex-1 border-4 border-transparent ${i < 3 ? innerBorderW : ''}`}
                  />
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Mailing Panel Preview ── */}
        <div className="mb-10 max-w-5xl mx-auto">
          <div className="flex items-center gap-3 mb-5 bg-brand-blue text-white px-5 py-3 rounded-2xl">
            <span className="font-black uppercase tracking-widest text-sm">Mailing Panel</span>
            <div className="flex-1 h-px bg-white/20" />
            <span className="text-xs font-black uppercase tracking-widest text-white/70">Back of mailer · USPS presorted standard</span>
          </div>
          <div className={`relative bg-white ${borderW} border-brand-brown rounded-[1.5rem] shadow-[24px_24px_0px_rgba(30,58,95,0.08)] overflow-hidden flex`} style={{ height: '120px' }}>
            <div className={`w-[35%] flex flex-col justify-center px-4 md:px-6 ${innerBorderW}`}>
              <span className="font-black text-brand-brown uppercase text-[9px] md:text-xs tracking-tighter leading-tight">Mailbox Digital</span>
              <span className="text-brand-brown/50 text-[7px] md:text-[9px] font-bold uppercase tracking-wide leading-tight mt-0.5">{cityName}, {cityState}</span>
              <span className="text-brand-brown/30 text-[7px] font-bold uppercase tracking-wide mt-0.5 hidden md:block">mailboxdigital.com</span>
            </div>
            <div className={`w-[28%] flex items-center justify-center ${innerBorderW}`}>
              <div className="border-[2px] border-brand-brown px-2 py-1.5 text-center">
                <span className="block text-[7px] md:text-[8px] font-black uppercase text-brand-brown tracking-tight leading-tight">PRESORTED</span>
                <span className="block text-[7px] md:text-[8px] font-black uppercase text-brand-brown tracking-tight leading-tight">STANDARD</span>
                <div className="border-t border-brand-brown/30 my-1" />
                <span className="block text-[6px] md:text-[7px] font-bold text-brand-brown/60 uppercase leading-tight">U.S. POSTAGE</span>
                <span className="block text-[6px] md:text-[7px] font-bold text-brand-brown/60 uppercase leading-tight">PAID</span>
                <span className="block text-[6px] md:text-[7px] font-bold text-brand-brown/40 uppercase leading-tight mt-0.5">PHOENIX, AZ</span>
              </div>
            </div>
            <div className="flex-1 flex flex-col items-center justify-center px-2 gap-1">
              <div className="flex items-center gap-2 mb-0.5">
                <QRCodeSVG
                  value={`${window.location.origin}${window.location.pathname}#/${citySlug}/directory`}
                  size={36}
                  fgColor="#1E3A5F"
                  bgColor="transparent"
                  level="M"
                />
                <div className="text-left">
                  <span className="block text-[6px] font-black uppercase text-brand-brown/60 tracking-widest leading-tight">Scan to view</span>
                  <span className="block text-[6px] font-black uppercase text-brand-brown/60 tracking-widest leading-tight">your vendors</span>
                </div>
              </div>
              <MailBarcode />
              <span className="text-[6px] md:text-[7px] font-black uppercase text-brand-brown/40 tracking-widest text-center leading-tight">
                LOCAL POSTAL CUSTOMER<br />{cityName.toUpperCase()}, {cityState}
              </span>
            </div>
          </div>
        </div>

        {/* ── Zip criteria ── */}
        <div className="max-w-5xl mx-auto mb-16">
          <div className="flex flex-col md:flex-row items-center gap-6 bg-brand-cream border-2 border-brand-brown/10 rounded-[2rem] px-8 py-6">
            <SafeIcon icon={FiShield} className="text-brand-blue text-3xl shrink-0" />
            <div>
              <p className="font-black text-brand-brown uppercase tracking-tight text-base mb-1">Our Zip Codes Are Hand-Selected — Not Random</p>
              <p className="font-bold text-brand-brown/60 leading-relaxed text-sm">
                We target neighborhoods based on household income, homeownership rates, and local service demand. Your ad reaches the right doors, not every door.
              </p>
            </div>
          </div>
        </div>

      </div>

      {/* ── Claim modal ── */}
      <AnimatePresence>
        {selectedSlot && (
          <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
              onClick={() => setSelectedSlot(null)}
              className="absolute inset-0 bg-brand-dark/80 backdrop-blur-md"
            />
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 30 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 30 }}
              className="relative bg-brand-cream border-[6px] border-brand-brown rounded-[3rem] shadow-[20px_20px_0px_#4A3320] w-full max-w-lg p-12 text-center"
            >
              <button onClick={() => setSelectedSlot(null)} className="absolute top-6 right-6 text-brand-brown">
                <SafeIcon icon={FiX} className="text-4xl" />
              </button>
              <div className="w-20 h-20 bg-[#1877F2]/10 rounded-2xl flex items-center justify-center mx-auto mb-8 text-[#1877F2] text-4xl">
                <SafeIcon icon={FiFacebook} />
              </div>
              <h3 className="text-4xl font-black text-brand-brown uppercase leading-none mb-6 italic tracking-tighter">
                Claim {selectedSlot.category}!
              </h3>
              <p className="text-brand-brown/70 font-bold text-lg mb-10 leading-relaxed">
                Message Sean on Facebook to lock in this category for {cityName.toUpperCase()}.
              </p>
              <a
                href="https://www.facebook.com/thegyrohero"
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full bg-[#1877F2] text-white font-black text-2xl uppercase py-6 rounded-2xl border-[6px] border-brand-brown shadow-[8px_8px_0px_#4A3320] transition-all"
              >
                Message Sean on FB
              </a>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </section>
  );
}
